'use client';

import { css } from '@bts-web/utils-style-engine';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { TFunction } from 'i18next';
import { getAppConfig } from '@bts-web/core-app-config';
import { AlertBox } from '../../../common/components/AlertBox/AlertBox';
import { Drawer } from '../../../common/components/Drawer/Drawer';
import { useDrawerMetadata } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { ButtonBase } from '../../../common/components/ButtonBase/ButtonBase';
import { getTradeAssetSelectorTranslations } from '../../ChangeCurrencyOrSwap/changeCurrencyUtils';

interface BankAccountDetailsDrawerProps {
  bankAccountHolder: string;
  accountNumber: string;
  bankName: string;
}

export const BankAccountDetailsDrawer = ({
  bankAccountHolder,
  accountNumber,
  bankName,
}: BankAccountDetailsDrawerProps) => {
  const { t } = useClientTranslation();

  const { closeThisDrawer } = useDrawerMetadata();

  const translations = getTradeAssetSelectorTranslations(t as TFunction);

  const { supportPhoneNumber, supportEmail } = getAppConfig();

  const rows = [
    {
      key: 'bankAccountHolder',
      label: translations.bankAccountHolder,
      value: bankAccountHolder,
    },
    { key: 'account', label: translations.account, value: accountNumber },
    { key: 'bankName', label: translations.bankName, value: bankName },
  ];

  return (
    <Drawer title={translations.details}>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'medium',
            px: 'medium',
            py: 'medium',
            flex: 1,
          })}
        >
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: 'extra_small_2',
              textAlign: 'center',
            })}
          >
            <h3
              className={css({
                fontSize: 'headline.medium_medium',
                fontWeight: 'headline.medium_medium',
                lineHeight: 'headline.medium_medium',
                letterSpacing: 'headline.medium_medium',
                color: 'neutrals.text_primary',
              })}
            >
              {translations.onlineTransfer}
            </h3>
            <p
              className={css({
                fontSize: 'label.medium_medium',
                fontWeight: 'label.medium_medium',
                lineHeight: 'label.medium_medium',
                letterSpacing: 'label.medium_medium',
                color: 'neutrals.text_secondary',
              })}
            >
              {translations.onlineTransferSubtitle}
            </p>
          </div>

          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
            })}
          >
            {rows.map(({ key, label, value }) => (
              <div
                key={key}
                className={css({
                  display: 'flex',
                  gap: 'medium',
                  alignItems: 'center',
                  minHeight: '64px',
                  py: 'small',
                  borderBottom: '1px solid',
                  borderColor: 'neutrals.divider',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                })}
              >
                <span
                  className={css({
                    width: '144px',
                    flexShrink: 0,
                    fontSize: 'body.medium',
                    fontWeight: 'body.medium',
                    lineHeight: 'body.medium',
                    letterSpacing: 'body.medium',
                    color: 'neutrals.text_secondary',
                  })}
                >
                  {label}
                </span>
                <span
                  className={css({
                    flex: 1,
                    textAlign: 'end',
                    fontSize: 'label.medium_medium',
                    fontWeight: 'label.medium_medium',
                    lineHeight: 'label.medium_medium',
                    letterSpacing: 'label.medium_medium',
                    color: 'neutrals.text_primary',
                    wordBreak: 'break-word',
                  })}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>

          <AlertBox variant="info">
            {translations.onlineTransferNameWarning}
          </AlertBox>

          {(supportEmail || supportPhoneNumber) && (
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: 'extra_small_2',
                alignItems: 'center',
                fontSize: 'body.medium_medium',
                fontWeight: 'body.medium_medium',
                lineHeight: 'body.medium_medium',
                letterSpacing: 'body.medium_medium',
                color: 'neutrals.text_secondary',
              })}
            >
              <span>{translations.onlineTransferContactSupport}</span>
              <span>{translations.onlineTransferContactTitle}</span>
              {supportPhoneNumber && (
                <span>
                  {translations.onlineTransferPhone}:{' '}
                  <a
                    className={css({
                      color: 'brand.text_primary',
                      textDecoration: 'underline',
                    })}
                    href={`tel:${supportPhoneNumber.replace(/[^\d+]/g, '')}`}
                  >
                    {supportPhoneNumber}
                  </a>
                </span>
              )}
              {supportEmail && (
                <span>
                  {translations.onlineTransferEmail}:{' '}
                  <a
                    className={css({
                      color: 'brand.text_primary',
                      textDecoration: 'underline',
                    })}
                    href={`mailto:${supportEmail}`}
                  >
                    {supportEmail}
                  </a>
                </span>
              )}
            </div>
          )}
        </div>

        <div
          className={css({
            px: 'medium',
            paddingTop: 'small',
            paddingBottom: 'medium',
          })}
        >
          <ButtonBase
            visual="accent"
            size="large"
            fullWidth
            onClick={closeThisDrawer}
          >
            {translations.close}
          </ButtonBase>
        </div>
      </div>
    </Drawer>
  );
};
